import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FileText, ArrowLeft } from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-700">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-700 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900"><BrandTitle>Terms of Service</BrandTitle></h1>
              <p className="text-slate-600 text-sm">Last updated: May 7, 2026</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <div className="space-y-8 text-slate-700">

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
                <p>
                  By accessing or using the services ("Services") provided by <strong className="text-emerald-700">CARTIEAI LLC</strong>, a Texas limited liability company ("CARTIEAI", "we", "our", or "us"), you agree to these Terms of Service ("Terms") and our <Link to="/privacy-policy" className="text-emerald-700">Privacy Policy</Link>. If you don't agree, please don't use our Services. These Terms cover three things: (a) the authenticated CARTIEAI application, (b) our public marketing site, and (c) free public tools we publish (see Section 6).
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Description of Services</h2>
                <p>
                  CARTIEAI is a cloud financial intelligence platform that helps engineering and finance teams understand and reduce cloud spending. We integrate with major cloud and SaaS providers — currently AWS, Azure, Google Cloud, Snowflake, Databricks, DigitalOcean and MongoDB Atlas — and read your billing data through read-only APIs.
                </p>
                <p className="mt-3">Core capabilities include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Multi-cloud cost dashboards and unit-economics analysis</li>
                  <li>AI-assisted cost recommendations, anomaly detection and forecasts</li>
                  <li>Customer-level profitability (Stripe-powered Cost-per-Customer P&amp;L)</li>
                  <li>PR-time Terraform cost prediction</li>
                  <li>Audit logs, role-based access control, MFA and SSO</li>
                  <li>CSV / FOCUS / PDF exports and scheduled email digests</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Account Registration</h2>
                <p>To use the authenticated Services you must:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate registration information</li>
                  <li>Keep your account credentials secure</li>
                  <li>Notify us immediately of any unauthorised access</li>
                  <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
                  <li>Be authorised to act on behalf of any organisation you connect</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Acceptable Use</h2>
                <p>You agree not to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe intellectual-property rights</li>
                  <li>Attempt to gain unauthorised access to our systems or another tenant's data</li>
                  <li>Interfere with or disrupt our Services (including DDoS, scraping at abusive volumes, or load-testing without prior arrangement)</li>
                  <li>Reverse engineer, decompile or attempt to extract source code from our binaries</li>
                  <li>Submit data you do not have the right to submit</li>
                  <li>Use the Services to develop a directly competing product</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Cloud-Provider &amp; Stripe Integration</h2>
                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">5.1 Read-only access</h3>
                <p>
                  All cloud and SaaS integrations are read-only by design. By connecting an account you confirm that you have authority to grant access and that the credentials you provide use the minimum required permissions. You can revoke access at any time from your provider's console.
                </p>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">5.2 Stateless credentials</h3>
                <p>
                  Cloud-provider keys you paste into a one-shot audit tool are held in memory only and discarded immediately after the scan. We never write them to our database, disk or logs.
                </p>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">5.3 Stripe restricted keys</h3>
                <p>
                  Our Customer P&amp;L feature requires a Stripe <em>restricted key</em> with read-only scopes (<code className="text-xs bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded">customer:read</code>, <code className="text-xs bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded">subscription:read</code>, <code className="text-xs bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded">invoice:read</code>). The key is stored encrypted at rest with strict tenant isolation. We will never request write permissions or attempt actions outside the documented read scopes. You can revoke the key from your Stripe dashboard at any time.
                </p>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">5.4 No infrastructure changes</h3>
                <p>
                  Recommendations, anomaly alerts and what-if scenarios are advisory only. We do not modify your cloud resources unless you explicitly enable an opt-in automation (e.g. the Resource Scheduler), in which case the actions performed are clearly described before activation.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Free Public Tools (No Account Required)</h2>
                <p>We publish a small set of free, no-signup tools as part of our marketing — including the <Link to="/pr-cost-predictor" className="text-emerald-700">PR-Time Cost Predictor</Link> and the <Link to="/trust" className="text-emerald-700">Trust page</Link> live security checks. Use of these tools is governed by these Terms with the following clarifications:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The tools are provided as-is for evaluation and educational purposes</li>
                  <li>You must not submit credentials, customer PII, or other sensitive data — Terraform plans should contain configuration only</li>
                  <li>We may rate-limit, throttle or block requests that abuse the service</li>
                  <li>We may change, deprecate or remove any free tool at any time</li>
                  <li>Pricing data returned by the calculator is for informational purposes only and does not constitute financial or commercial advice (see Section 9)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Subscriptions, Payments &amp; Free Tier</h2>
                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">7.1 Free tier</h3>
                <p>We offer a generous free tier suitable for individual engineers and small teams. Limits are published on our <Link to="/pricing" className="text-emerald-700">Pricing page</Link> and may be adjusted with reasonable notice.</p>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">7.2 Paid plans</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Paid subscriptions are billed monthly or annually via Stripe</li>
                  <li>Prices may change with at least 30 days written notice for existing customers</li>
                  <li>You are responsible for any applicable sales tax, VAT or similar levies</li>
                </ul>

                <h3 className="text-lg font-medium text-slate-900 mt-4 mb-2">7.3 Refunds</h3>
                <p>Our 14-day full-refund window and cancellation rules are described in our <Link to="/refund" className="text-emerald-700">Refund Policy</Link>, which forms part of these Terms.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Service Availability &amp; Support</h2>
                <p>
                  We aim for high availability and publish live status at <Link to="/status" className="text-emerald-700">cartieai.com/status</Link>. The Services are currently provided on a <strong>best-effort basis</strong> — we do not commit to a contractual SLA on Free or Starter plans. Customers on annual Pro or Enterprise contracts may negotiate a written SLA with credits.
                </p>
                <p className="mt-3">
                  Support is offered via email at <a href="mailto:hello@cartieai.com" className="text-emerald-700">hello@cartieai.com</a>; our founder reads every message and replies within one business day for paid customers.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">9. AI-Assisted Recommendations — Disclaimer</h2>
                <p>
                  Many CARTIEAI features (Cost Bot, Invoice Autopsy, Board Deck, Customer P&amp;L recommendations, the PR-Time Cost Predictor, etc.) use AI models to generate suggestions, summaries and forecasts. AI output is informational only — it is <strong>not</strong> financial, legal, tax or investment advice, and it can occasionally be incorrect or incomplete.
                </p>
                <p className="mt-3">
                  You are responsible for reviewing AI suggestions before acting on them, particularly for actions with a financial or operational impact (e.g. resizing instances, terminating workloads, signing vendor contracts). We strongly recommend a human reviewer in your normal change-management process before applying any AI recommendation.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Intellectual Property</h2>
                <p>
                  All content, features and functionality of our Services — excluding your data — are owned by CARTIEAI and protected by copyright, trademark and other intellectual-property laws.
                </p>
                <p className="mt-3">
                  <strong className="text-white">You retain ownership of your data.</strong> You grant us a non-exclusive, worldwide licence to host, process and display your data solely for the purpose of operating the Services on your behalf. We will not use your raw data to train AI models for the benefit of other customers.
                </p>
                <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
                  <p className="text-emerald-300 font-semibold mb-2" data-testid="terms-trademark-transparency-heading">10.1 Trademarks &amp; patents — what we actually claim</p>
                  <p className="mb-2">
                    We believe in being honest about what is genuinely ours. The <strong className="text-white">™</strong> symbol on this site appears only on (a) the brand name <strong className="text-white">CARTIEAI</strong>, and (b) feature names that map to inventions we have an active patent application on. As of <span className="text-emerald-300">February 2026</span>, our active patent application covers:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-slate-700 mb-2">
                    <li><strong className="text-white">Budget Negotiator™</strong> — Finance ↔ Engineering auto-negotiation at pull-request time, signing a budget contract on every merge before the resource is provisioned (provisional patent in preparation).</li>
                  </ul>
                  <p className="text-slate-600 mb-2">
                    Our other named features (CARTIE Lens, Cost-Bisect, Cost Causality, Causal FinOps Bot, Decision Simulator, K8s Shapley Attribution, Shared Resource Allocator, Reverse-Tag Inferencer, Tag Canonicaliser, Batch Router, QA Failover, Forward Cost Predictor, Real-Time Unit Economics, Multi-Currency P&amp;L, Carbon-Cost Joint Optimizer, Scope-3 Carbon, GPU Spot-Bidding, Safe Autonomy, FinOps Copilot) are <strong className="text-white">proprietary engines</strong> we built internally. They contain trade-secret implementations differentiated by our specific algorithms, data pipelines, and integration architecture. We do <strong className="text-white">not</strong> claim broad patents on industry-standard FinOps practice — instead we compete on execution velocity, AI-native focus, and the depth of our specific implementations.
                  </p>
                  <p className="text-slate-600">
                    Everything else on the platform — anomaly detection, RI/SP optimization, budget alerts, rightsizing, peer benchmarks, autonomous remediation, AI-generated reports, token leak detection, retry-cost audits, drift alerts, prompt-cost calculators, fine-tuning ROI math, per-customer usage heatmaps, streaming-vs-batch diffs, etc. — is well-known FinOps and LLMOps practice that the industry has converged on. We deliberately do <strong className="text-white">not</strong> mark those features with ™. If you see ™ on the site, it means we have an active patent application on that specific method. If you don&apos;t, it means we&apos;re building on standard industry practice and we&apos;re not pretending otherwise.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Data Export &amp; Deletion</h2>
                <p>
                  You can export your account data in CSV, JSON or FOCUS-aligned formats from your account settings at any time. On cancellation we delete your data within 30 days, except for records we are legally required to retain (e.g. financial records).
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Disclaimer of Warranties</h2>
                <p>
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED. WE DISCLAIM ALL IMPLIED WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">13. Limitation of Liability</h2>
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, CARTIEAI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES. OUR AGGREGATE LIABILITY ARISING OUT OF THESE TERMS WILL NOT EXCEED THE FEES YOU PAID US IN THE 12 MONTHS PRIOR TO THE CLAIM (OR USD $100 IF YOU ARE ON THE FREE TIER OR USING ONLY FREE PUBLIC TOOLS).
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">14. Indemnification</h2>
                <p>
                  You agree to indemnify and hold CARTIEAI, its officers, employees and contractors harmless from any third-party claims, damages or expenses arising from (a) your use of the Services, (b) data you submit, or (c) your violation of these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">15. Termination</h2>
                <p>
                  We may suspend or terminate your access to the Services for material violations of these Terms, after written notice and a reasonable cure period where practical. You may cancel your account at any time through account settings or by emailing us. On termination, Sections 10–14 and 16–18 survive.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">16. Changes to These Terms</h2>
                <p>
                  We may update these Terms as our business and the regulatory landscape evolve. For material changes we will notify account holders via email at least 14 days before the new version takes effect. The "Last updated" date at the top reflects the current version. Continued use after the effective date constitutes acceptance.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">17. Governing Law &amp; Disputes</h2>
                <p>
                  These Terms are governed by the laws of the State of Texas, United States, without regard to conflict-of-law rules. The state and federal courts located in Travis County, Texas have exclusive jurisdiction over any dispute, except that either party may seek injunctive relief in any court of competent jurisdiction. We will always try to resolve concerns informally first — please email us before initiating any formal action.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">18. Contact Information</h2>
                <p>For any question about these Terms:</p>
                <div className="mt-4 p-4 bg-slate-800/50 rounded-lg">
                  <p><strong className="text-white">Email:</strong> <a href="mailto:hello@cartieai.com" className="text-emerald-700">hello@cartieai.com</a></p>
                  <p className="mt-2"><strong className="text-white">Phone:</strong> <a href="tel:+15126217610" className="text-emerald-700">+1 (512) 621-7610</a></p>
                  <p className="mt-2"><strong className="text-white">Legal entity:</strong> CARTIEAI LLC</p>
                  <p className="mt-2"><strong className="text-white">Postal:</strong> CARTIEAI LLC · Texas, United States</p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 text-slate-600 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-6 h-6" />
            <span className="text-sm">
              <span className="text-white">CARTIE<span className="text-emerald-700">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
            </span>
          </div>
          <p className="text-sm" data-testid="terms-footer-entity">© 2026 CARTIEAI LLC · Texas, USA · All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
